import { motion } from "framer-motion";

import SmartLink from "@/components/SmartLink";
import { Button } from "@/components/ui/button";
import { iconRegistry } from "@/content/iconRegistry";
import { trackCheckoutClick } from "@/lib/conversionTracking";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

function HeroAction({ cta, variant = "default", className = "", checkoutProduct = "workshop" }) {
  if (!cta) {
    return null;
  }

  const Icon = cta.icon ? iconRegistry[cta.icon] : null;

  return (
    <Button
      asChild
      size="lg"
      variant={variant}
      className={className}
    >
      <SmartLink
        href={cta.href}
        external={cta.external}
        onClick={() => trackCheckoutClick({ product: checkoutProduct, label: cta.label, href: cta.href, location: "hero" })}
      >
        {cta.label}
        {Icon ? <Icon className="ml-2 h-4 w-4" /> : null}
      </SmartLink>
    </Button>
  );
}

function HeroMedia({ media }) {
  if (!media) {
    return null;
  }

  const FloatingIcon = media.floatingBadge ? iconRegistry[media.floatingBadge.icon] : null;
  const shouldPlaceBadgeBelowImage = media.type === "image" && media.imageFit === "contain";

  if (media.type === "mockup") {
    const MockupIcon = iconRegistry[media.mockupIcon];

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative"
      >
        <div className="relative rounded-3xl overflow-hidden shadow-medium bg-gradient-to-br from-secondary via-secondary/90 to-secondary/65 p-8 border border-white/10">
          <div className="bg-card/95 rounded-2xl shadow-soft p-4 border border-white/10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-highlight/90" />
              <div className="w-3 h-3 rounded-full bg-highlight/80" />
              <div className="w-3 h-3 rounded-full bg-white/70" />
            </div>
            <div className="space-y-3">
              <div className="h-8 bg-gradient-to-r from-secondary/80 via-secondary/55 to-highlight/30 rounded-lg" />
              <div className="h-4 bg-white/10 rounded w-3/4" />
              <div className="h-4 bg-white/10 rounded w-1/2" />
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="h-20 bg-background/70 rounded-lg" />
                <div className="h-20 bg-background/70 rounded-lg" />
              </div>
              <div className="h-10 bg-secondary rounded-full w-1/2 mx-auto mt-4" />
            </div>
          </div>
          <div className="absolute -top-2 -right-2 w-16 h-16 bg-highlight/30 rounded-full flex items-center justify-center border border-white/10">
            <MockupIcon className="w-8 h-8 text-white" />
          </div>
          {media.mockupLabel ? (
            <div className="mt-6 text-center">
              <p className="font-sans font-semibold text-white">{media.mockupLabel}</p>
              {media.mockupCaption ? (
                <p className="font-body text-sm text-white/72 mt-1">{media.mockupCaption}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {media.floatingBadge ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="absolute -bottom-4 -left-4 md:bottom-4 md:-left-8 bg-card/95 rounded-2xl p-4 shadow-medium border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary/70 flex items-center justify-center">
                <FloatingIcon className="w-5 h-5 text-highlight" />
              </div>
              <div>
                <p className="font-sans font-semibold text-foreground text-sm">{media.floatingBadge.title}</p>
                <p className="font-body text-xs text-muted-foreground">{media.floatingBadge.description}</p>
              </div>
            </div>
          </motion.div>
        ) : null}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="relative"
    >
      <div className="relative rounded-3xl overflow-hidden shadow-medium">
        <img
          src={media.imageSrc}
          alt={media.imageAlt}
          className={
            media.imageFit === "contain"
              ? "w-full h-auto object-contain"
              : "w-full h-auto object-cover aspect-[4/3]"
          }
        />
      </div>

      {media.floatingBadge ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className={
            shouldPlaceBadgeBelowImage
              ? "mt-4 bg-card/95 rounded-2xl p-4 shadow-medium border border-white/10"
              : "absolute -bottom-4 -left-4 md:bottom-8 md:-left-8 bg-card/95 rounded-2xl p-4 shadow-medium border border-white/10"
          }
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary/70 flex items-center justify-center">
              <FloatingIcon className="w-5 h-5 text-highlight" />
            </div>
            <div>
              <p className="font-sans font-semibold text-foreground text-sm">{media.floatingBadge.title}</p>
              <p className="font-body text-xs text-muted-foreground">{media.floatingBadge.description}</p>
            </div>
          </div>
        </motion.div>
      ) : null}
    </motion.div>
  );
}

export default function HeroSection({ content, dataTestId, checkoutProduct = "workshop" }) {
  const backgroundClassName =
    content.background === "gradient"
      ? "relative overflow-hidden"
      : content.background === "muted"
        ? "bg-muted/50"
        : "";
  const footerNotes = content.footerNote
    ? content.footerNote.split("|").map((item) => item.trim()).filter(Boolean)
    : [];
  const detailRows = content.detailRows?.filter((item) => item.label && item.value) || [];
  const secondaryDescriptionParagraphs = content.secondaryDescription
    ? content.secondaryDescription.split("\n\n").map((item) => item.trim()).filter(Boolean)
    : [];

  return (
    <section className={`py-16 md:py-24 lg:py-32 ${backgroundClassName}`.trim()} data-testid={dataTestId}>
      {content.background === "gradient" ? (
        <div className="absolute inset-0 bg-gradient-to-br from-secondary/35 via-transparent to-secondary/12" />
      ) : null}
      <div className={`max-w-7xl mx-auto px-6 md:px-12 ${content.background === "gradient" ? "relative" : ""}`.trim()}>
        {content.align === "center" ? (
          <motion.div initial="initial" animate="animate" variants={stagger} className="text-center max-w-4xl mx-auto">
            <motion.span
              variants={fadeUp}
              className="inline-block px-4 py-1.5 rounded-full bg-highlight/18 text-foreground font-body text-sm font-medium mb-6 border border-highlight/40 shadow-soft"
            >
              {content.eyebrow}
            </motion.span>
            <motion.h1
              variants={fadeUp}
              className="font-sans text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
            >
              {content.title}{" "}
              {content.accent ? <span className="text-highlight">{content.accent}</span> : null}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="font-body text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto"
            >
              {content.description}
            </motion.p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="initial" animate="animate" variants={stagger}>
              <motion.span
                variants={fadeUp}
                className="inline-block px-4 py-1.5 rounded-full bg-highlight/18 text-foreground font-body text-sm font-medium mb-6 border border-highlight/40 shadow-soft"
              >
                {content.eyebrow}
              </motion.span>
              <motion.h1
                variants={fadeUp}
                className="font-sans text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6"
              >
                {content.title}{" "}
                {content.accent ? <span className="text-highlight">{content.accent}</span> : null}
              </motion.h1>
              <motion.p variants={fadeUp} className="font-body text-lg text-muted-foreground leading-relaxed mb-4 max-w-xl">
                {content.description}
              </motion.p>
              {secondaryDescriptionParagraphs.length ? (
                <motion.div variants={fadeUp} className="font-body text-base text-muted-foreground/85 leading-relaxed mb-8 space-y-3">
                  {secondaryDescriptionParagraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </motion.div>
              ) : null}
              {detailRows.length ? (
                <motion.div
                  variants={fadeUp}
                  className="mb-8 grid max-w-2xl grid-cols-1 gap-4 sm:grid-cols-2"
                >
                  {detailRows.map((item) => (
                    <div
                      key={item.label}
                      className="rounded-3xl bg-card/92 border border-border shadow-soft p-5 md:p-6"
                    >
                      <p className="font-sans text-sm font-semibold uppercase tracking-[0.12em] text-foreground/72 mb-3">
                        {item.label}
                      </p>
                      <p className="font-body text-sm md:text-base leading-relaxed text-foreground">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </motion.div>
              ) : null}
              {footerNotes.length ? (
                <motion.div variants={fadeUp} className="flex flex-wrap gap-3 mb-8">
                  {footerNotes.map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center rounded-full bg-card/88 px-4 py-2 text-sm font-medium text-foreground shadow-soft border border-border"
                    >
                      {item}
                    </span>
                  ))}
                </motion.div>
              ) : null}
              {(content.primaryCta || content.secondaryCta) ? (
                <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mb-6">
                  <HeroAction
                    cta={content.primaryCta}
                    checkoutProduct={checkoutProduct}
                    className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-8 btn-hover w-full sm:w-auto"
                  />
                  <HeroAction
                    cta={content.secondaryCta}
                    checkoutProduct={checkoutProduct}
                    variant="outline"
                    className="rounded-full border-transparent bg-secondary hover:bg-secondary/85 text-white font-medium px-8 btn-hover w-full sm:w-auto"
                  />
                </motion.div>
              ) : null}
            </motion.div>
            <HeroMedia media={content.media} />
          </div>
        )}
      </div>
    </section>
  );
}
